// Post-signup celebration modal — shown ONCE the first time a new user
// lands on /dashboard. Confetti + free-plan breakdown + "Start Automating"
// CTA. Persisted server-side via POST /api/onboarding/trial-welcome-shown
// so it never re-renders.
import { useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { ArrowRight, X, Lock, Check } from "lucide-react";
import { Button } from "../ui/button";

// Matches the v4 pricing matrix (backend/subscription/config.py). The follow
// gate MOVED from LOCKED to INCLUDED in Aug 2026 — it is now free on every
// plan and is the headline free feature, so listing it as paid here would be
// the first thing a new user reads and it would be wrong.
const INCLUDED = [
  "Unlimited DMs",
  "Unlimited automations",
  "Follow gate",
  "Comment → DM & story replies",
  "Link in bio + lead capture",
  "AI Reel analysis (5 credits)",
  "Weekly AI report + trends",
];

const LOCKED = [
  "Remove Tadlo branding (paid)",
  "Priority DM delivery (paid)",
  "Re-trigger users (paid)",
];

// Confetti dot palette — kept inline, no external library.
const CONFETTI_COLORS = [
  "#7F77DD", "#1D9E75", "#EF9F27", "#E5484D",
  "#3b82f6", "#a855f7", "#22d3ee", "#fb7185",
];

function ConfettiPiece({ idx }) {
  // Deterministic per-index offset so the burst feels organic
  // without re-rendering on every mount.
  const left = (idx * 37) % 100;
  const delay = (idx % 8) * 0.18;
  const dur = 3.5 + (idx % 5) * 0.4;
  const color = CONFETTI_COLORS[idx % CONFETTI_COLORS.length];
  const size = 6 + (idx % 4) * 2;
  return (
    <span
      aria-hidden="true"
      className="absolute top-[-12px] rounded-[2px] pointer-events-none"
      style={{
        left: `${left}%`,
        width: size,
        height: size * 0.6,
        background: color,
        animation: `tadlo-confetti-fall ${dur}s linear ${delay}s infinite`,
        opacity: 0.9,
      }}
    />
  );
}

export default function TrialWelcomeModal({ onStart }) {
  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // ESC dismiss → still counts as "shown" so we never re-prompt.
  useEffect(() => {
    const h = (e) => e.key === "Escape" && onStart();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onStart]);

  const confetti = useMemo(
    () => Array.from({ length: 28 }, (_, i) => i),
    [],
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="fixed inset-0 z-[210] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="trial-welcome-overlay"
    >
      {/* Confetti animation keyframes — scoped via <style> so we don't
          touch the global stylesheet. */}
      <style>{`
        @keyframes tadlo-confetti-fall {
          0%   { transform: translateY(-20px) rotate(0deg); opacity: 0; }
          10%  { opacity: 1; }
          100% { transform: translateY(640px) rotate(540deg); opacity: 0; }
        }
      `}</style>

      <motion.div
        initial={{ scale: 0.92, opacity: 0, y: 12 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.92, opacity: 0 }}
        transition={{ type: "spring", damping: 24, stiffness: 280 }}
        className="relative w-full max-w-[440px] rounded-2xl bg-background border border-border shadow-2xl overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="trial-welcome-title"
        data-testid="trial-welcome-modal"
      >
        {/* Confetti container — clipped to header band */}
        <div className="relative h-[180px] overflow-hidden bg-gradient-to-br from-primary/[0.18] via-violet-500/[0.10] to-pink-500/[0.08]">
          {confetti.map((i) => <ConfettiPiece key={i} idx={i} />)}

          {/* Close X — purely defensive, also marks shown */}
          <button
            type="button"
            onClick={onStart}
            aria-label="Close"
            data-testid="trial-welcome-close"
            className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-background/80 hover:bg-background text-muted-foreground hover:text-foreground flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Hero */}
          <div className="relative z-[1] flex flex-col items-center justify-center h-full px-6 text-center">
            <div className="text-5xl mb-2" aria-hidden="true">🎉</div>
            <h2
              id="trial-welcome-title"
              className="text-[22px] sm:text-2xl font-semibold leading-tight"
              style={{ fontFamily: "Outfit" }}
              data-testid="trial-welcome-headline"
            >
              Your free plan is live — forever
            </h2>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 pt-4 pb-6 space-y-4">
          <p className="text-sm text-muted-foreground text-center">
            Start automating your Instagram growth today.
          </p>

          {/* Trial details card */}
          <div className="rounded-xl bg-secondary/40 border border-border/60 p-4 space-y-3" data-testid="trial-welcome-details">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
              What's included, free
            </div>
            <ul className="space-y-1.5 text-sm">
              {INCLUDED.map((line) => (
                <li key={line} className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
              {LOCKED.map((line) => (
                <li key={line} className="flex items-center gap-2 text-muted-foreground">
                  <Lock className="w-3.5 h-3.5 shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>

            <div className="pt-2 border-t border-border/50 flex items-center gap-2">
              <span className="text-[11px] font-semibold text-primary tabular-nums">
                Unlimited DMs
              </span>
              <span className="text-[11px] text-muted-foreground">
                &middot; no caps, no expiry, no card
              </span>
            </div>
          </div>

          <Button
            onClick={onStart}
            className="w-full h-11 text-sm font-medium bg-primary hover:bg-primary/90 text-primary-foreground"
            data-testid="trial-welcome-cta"
          >
            Start Automating
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <p className="text-[11px] text-muted-foreground text-center">
            Free forever &middot; No credit card required
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}
